import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIsMobile } from '../hooks/useIsMobile';
import axios from 'axios';
import { properties, plants } from '../services/api';
import GardenModal from './GardenModal';
import './AddTask.css';

function AddTask() {
  const navigate = useNavigate();
  const [userProperties, setUserProperties] = useState([]);
  const [userPlants, setUserPlants] = useState([]);
  const [loading, setLoading] = useState(true);
  const isMobile = useIsMobile();
  const [modal, setModal] = useState({ isOpen: false });
  const [formData, setFormData] = useState({
    propertyId: '',
    plantId: '',
    taskType: 'watering',
    title: '',
    description: '',
    scheduledFor: '',
    recurrence: 'none'
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [propsRes, plantsRes] = await Promise.all([
        properties.getAll(),
        plants.getAll()
      ]);

      setUserProperties(propsRes.data.properties);
      setUserPlants(plantsRes.data.plants);

      if (propsRes.data.properties.length > 0) {
        setFormData(prev => ({
          ...prev,
          propertyId: propsRes.data.properties[0].id
        }));
      }

      setLoading(false);
    } catch (error) {
      console.error('Error loading data:', error);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_API_URL}/task`,
        {
          propertyId: formData.propertyId,
          plantId: formData.plantId || null,
          taskType: formData.taskType,
          title: formData.title,
          description: formData.description,
          scheduledFor: formData.scheduledFor,
          recurrenceRule: formData.recurrence !== 'none' ? formData.recurrence : null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setModal({ isOpen: true, type: 'alert', message: 'Task created successfully!', onConfirm: () => { setModal({ isOpen: false }); navigate('/tasks'); } });

    } catch (error) {
      console.error('Error creating task:', error);
      setModal({ isOpen: true, type: 'alert', title: 'Error', message: 'Failed to create task', onConfirm: () => setModal({ isOpen: false }) });
    }
  };

  if (loading) {
    return <div className="add-task-loading">Loading...</div>;
  }

  if (userProperties.length === 0) {
    return (
      <div className={`add-task-container${isMobile ? ' add-task-container--mobile' : ''}`}>
        <GardenModal isOpen={modal.isOpen} type={modal.type} title={modal.title} message={modal.message} onConfirm={modal.onConfirm} onCancel={() => setModal({ isOpen: false })} />
        <h2>Add Task</h2>
        <div className="add-task-empty">
          <p>You need to create a property first.</p>
          <button className="add-task-empty-btn" onClick={() => navigate('/settings')}>
            Go to Properties
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`add-task-container${isMobile ? ' add-task-container--mobile' : ''}`}>
      <GardenModal isOpen={modal.isOpen} type={modal.type} title={modal.title} message={modal.message} onConfirm={modal.onConfirm} onCancel={() => setModal({ isOpen: false })} />
      <h2>Add New Task</h2>
      <p className="add-task-hint">* = required</p>

      <form onSubmit={handleSubmit} className="add-task-form">

        <div className="add-task-field">
          <label className="add-task-label">Property *</label>
          <select
            className="add-task-input"
            value={formData.propertyId}
            onChange={(e) => setFormData({...formData, propertyId: e.target.value})}
            required
          >
            {userProperties.map(prop => (
              <option key={prop.id} value={prop.id}>{prop.name}</option>
            ))}
          </select>
        </div>

        <div className="add-task-field">
          <label className="add-task-label">Plant</label>
          <select
            className="add-task-input"
            value={formData.plantId}
            onChange={(e) => setFormData({...formData, plantId: e.target.value})}
          >
            <option value="">All plants / General task</option>
            {userPlants.map(plant => (
              <option key={plant.id} value={plant.id}>{plant.common_name}</option>
            ))}
          </select>
        </div>

        <div className="add-task-field">
          <label className="add-task-label">Task Type</label>
          <select
            className="add-task-input"
            value={formData.taskType}
            onChange={(e) => setFormData({...formData, taskType: e.target.value})}
          >
            <option value="watering">Watering</option>
            <option value="fertilize">Fertilizing</option>
            <option value="pruning">Pruning</option>
            <option value="pest_control">Pest Control</option>
            <option value="mowing">Mowing</option>
            <option value="weeding">Weeding</option>
            <option value="harvesting">Harvesting</option>
            <option value="custom">Custom</option>
          </select>
        </div>

        <div className="add-task-field">
          <label className="add-task-label">Title *</label>
          <input
            className="add-task-input"
            type="text"
            value={formData.title}
            onChange={(e) => setFormData({...formData, title: e.target.value})}
            required
            placeholder="e.g., Water tomatoes, Mow lawn"
          />
        </div>

        <div className="add-task-field">
          <label className="add-task-label">Description</label>
          <textarea
            className="add-task-textarea"
            value={formData.description}
            onChange={(e) => setFormData({...formData, description: e.target.value})}
            rows="3"
          />
        </div>

        <div className="add-task-field">
          <label className="add-task-label">Scheduled For *</label>
          <input
            className="add-task-input"
            type="date"
            value={formData.scheduledFor}
            onChange={(e) => setFormData({...formData, scheduledFor: e.target.value})}
            required
          />
        </div>

        <div className="add-task-field add-task-field--last">
          <label className="add-task-label">Repeat</label>
          <select
            className="add-task-input"
            value={formData.recurrence}
            onChange={(e) => setFormData({...formData, recurrence: e.target.value})}
          >
            <option value="none">Does not repeat</option>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="biweekly">Every 2 weeks</option>
            <option value="monthly">Monthly</option>
          </select>
        </div>

        <div className="add-task-actions">
          <button type="submit" className="add-task-btn-create">
            Create Task
          </button>
          <button type="button" className="add-task-btn-cancel" onClick={() => navigate('/tasks')}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

export default AddTask;
